import { type BuiltInTool, OPEN_OBJECT_SCHEMA } from './types'

const SNAPSHOT =
    "`{ teams, members }`: each team's id, name and slug, and each member's id, name, email, persona, roles, and every task of a role they hold, whatever team they sit on (not OS tasks)."

/**
 * Read-only views of company state, so a run can name current blockers and open gates from the company as it is
 * rather than from memory. `os.directory` is here too: chat offers it, and a run answers it with the same snapshot
 * as `os.inspect_enterprise`.
 */
export const OS_SENSE_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.inspect_enterprise',
        family: 'os.sense',
        summary: 'A snapshot of the company: teams, members, roles, and the tasks of the roles each member holds.',
        description:
            'Read the company as it is now: every team (id, name, slug) and every member (id, name, email, persona, roles), with each member’s tasks: every task of a role they hold, whatever team they sit on (not OS tasks). The same snapshot as os.directory. Use this to name who owns what before you assign, route or escalate work. Read-only; pass no arguments.',
        inputSchema: OPEN_OBJECT_SCHEMA,
        returns: SNAPSHOT,
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        public: true,
    },
    {
        name: 'os.directory',
        family: 'os.sense',
        summary: 'Teams, members, roles, and the tasks of the roles each member holds: who to offload work to.',
        description:
            'List teams, members, roles, and each member’s tasks: every task of a role they hold, whatever team they sit on (not OS tasks). Use this to find someone to offload work to.',
        inputSchema: OPEN_OBJECT_SCHEMA,
        returns: `${SNAPSHOT} The same snapshot as \`os.inspect_enterprise\`.`,
        callers: ['run', 'chat'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        public: true,
        chatPrompt: 'os.directory lists colleagues and their tasks.',
    },
    {
        name: 'os.list_blockers',
        family: 'os.sense',
        summary:
            'What has stopped: failed, stalled, blocked and spend-paused runs, and stopped pipelines, with who owns each.',
        // #1011: the MCP server's wording, which says what Blockers holds and where waiting gates are instead; a
        // run reads the same sections (`RunsService.blockerView()`), and `returns` says what each caller gets.
        description:
            'What is stopping work right now, as the Blockers page shows it: failed runs not yet cleared (one page, ' +
            'with failedTotal counting them all), stalled, blocked and spend-paused runs, stopped pipelines, ' +
            'missing model tiers, exhausted LLM capacity, task definition problems, and teams with no lead, no ' +
            'metric or an overdue retro. Read-only: nothing is retried or cleared. Waiting gates are not here; ' +
            'use os.list_open_gates.',
        // The paging `GET /v1/blockers` takes (#1011), and every caller honours it: the MCP server and the API pass
        // it to the route, and a run's answer (`RunsService.blockerView()`) pages the same way. Only the default
        // page size differs: a run reads 100 failed runs a call, so one call is enough until `failedTotal` says
        // there are more.
        inputSchema: {
            type: 'object',
            properties: {
                failedPage: {
                    type: 'integer',
                    minimum: 1,
                    description:
                        'The page of failed runs to read, from 1. Defaults to 1. A page past the last reads the last.',
                },
                pageSize: {
                    type: 'integer',
                    minimum: 1,
                    maximum: 100,
                    description: 'Failed runs per page, up to 100. Defaults to 100 in a run, and to 20 over the API.',
                },
            },
            additionalProperties: false,
        },
        returns:
            'What the portal shows as blockers. To a run: `runs`, each stopped run once, oldest first, with `runId`, the `sections` it is listed in (failed, stopped, blocked, stalled, paused), `status`, `reason`, `blockClass`, its `task`, `team`, team `lead` and `assignee`, the `repo`, `issue` and `pr` from its input, `since`, `taskRetries` (how many times tasks have retried its chain) and `escalation` (to whom, `toYou`, the note, when) once someone escalated it; then `failedTotal` (every uncleared failed run), the `failedPage` read and its `pageSize`, the team-health rows (`missingLead`, `overdueRetro`, and `kpiHealth`: a KPI that needs attention, with its `condition` (`no_key_kpi`, `no_target`, `overdue` or `light`), `team`, `kpi`, `layer`, `light`, `trend` and `owner`), `spendEnabled` and `draining`. Over the API: the portal rows as they are. Waiting gates are not here: read them with `os.list_open_gates`.',
        callers: ['run', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: { method: 'GET', path: '/v1/blockers', scope: 'blockers:read' },
        public: true,
    },
    {
        name: 'os.list_open_gates',
        family: 'os.sense',
        summary: "Every gate waiting on a decision, oldest first: the CEO inbox's own rows.",
        // #1011: the MCP server's wording, which says what a waiting gate carries; a run and the API read the same
        // rows (`GatesService.listWaiting()`).
        description:
            'Every gate waiting on a decision, oldest first: the run and task it belongs to, the tool call it holds ' +
            '(toolName, payload), its summary when the run wrote one, who owns the decision, when it times out, ' +
            'and whether the run can be sent back with changes requested. Read-only: deciding a gate is not a ' +
            'tool here.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns:
            'Each waiting gate: `runId`, `taskSlug`, `toolName`, `owner`, `payload`, `summary` (when the run wrote one), `createdAt` and its age.',
        callers: ['run', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        // The query every call sends is written into the path: the API lists waiting gates for `status=waiting`.
        api: { method: 'GET', path: '/v1/gates?status=waiting', scope: 'gates:read' },
        public: true,
    },
    {
        name: 'os.list_waiting',
        family: 'os.sense',
        summary:
            'Open work nothing is working on: no live or queued run, no waiting decision, not on Blockers, not finished.',
        // #1457: the rows `GET /v1/waiting` serves, from the same rules (`RunsService.listWaiting()`).
        description:
            'Every piece of open work nothing is working on, as the Waiting list shows it: one row per issue, ' +
            'pull request or work item whose last run has ended, with no live or queued run, no decision waiting and nothing on ' +
            'Blockers, that is not finished: its last run did not end on an outcome its task declares as finishing ' +
            'the work, nobody has marked it finished, and no person cancelled its last run. Work stays listed ' +
            'however old it is. Read-only: nothing is restarted or finished. Pass no arguments.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns:
            '`{ rows, total }`, longest waiting first. Each row: `pipeline` (`owner/name#<number>` for an issue or pull request, `work:<id>` for a work item), `repo` and `number` (null for a work item), `workItem` and `workItemTitle` (the item and its title; null for an issue or pull request), the newest `runId` and its `taskId`, `endedAs` (how that run ended: `succeeded`, `failed`, `cancelled`, or `decision_rejected`), the `reason` it gave and what the work `waitsOn` (each null when it named none), `since` (when it ended) and `waitedMs`. `total` is the number of rows.',
        callers: ['run', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        // The route the portal's Waiting page reads. It rides `runs` (`SEGMENT_RESOURCES` in packages/db), so a grant
        // that reads runs reads it. Its two actions are `os.finish_waiting` and `os.restart_waiting`.
        api: { method: 'GET', path: '/v1/waiting', scope: 'runs:read' },
        public: true,
    },
]
